/**
 * Paste Strip Plugin for ProseMirror
 *
 * Strips formatting from pasted content, converting it to plain text
 * while preserving paragraph structure. This ensures that pasted content
 * from rich text sources (Word, Google Docs, web pages) doesn't bring
 * unwanted formatting into the document.
 *
 * Supports "Paste Special" (Ctrl+Shift+V / Cmd+Shift+V) to preserve
 * basic formatting (bold, italic, underline) while still sanitizing
 * the content for security.
 */

import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Slice, Fragment, type Node as ProseMirrorNode, type Mark } from "@tiptap/pm/model";
import type { EditorView } from "@tiptap/pm/view";
import DOMPurify from "dompurify";
import { schema } from "../schema";

export const pasteStripPluginKey = new PluginKey<PasteStripPluginState>(
  "pasteStrip"
);

interface PasteStripPluginState {
  shiftKeyDown: boolean;
}

/**
 * Create a plugin that strips formatting from pasted content.
 * Supports Ctrl+Shift+V for preserving basic formatting.
 */
export function createPasteStripPlugin(): Plugin<PasteStripPluginState> {
  // Track shift key state per view instance
  const viewShiftState = new WeakMap<EditorView, boolean>();

  return new Plugin<PasteStripPluginState>({
    key: pasteStripPluginKey,

    state: {
      init(): PasteStripPluginState {
        return { shiftKeyDown: false };
      },
      apply(_tr, value): PasteStripPluginState {
        return value;
      },
    },

    view(editorView) {
      // Initialize shift state for this view
      viewShiftState.set(editorView, false);

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Shift") {
          viewShiftState.set(editorView, true);
        }
      };

      const handleKeyUp = (e: KeyboardEvent) => {
        if (e.key === "Shift") {
          viewShiftState.set(editorView, false);
        }
      };

      // Handle blur to reset shift state (user switches tabs while holding shift)
      const handleBlur = () => {
        viewShiftState.set(editorView, false);
      };

      // Add listeners to the editor's DOM element
      editorView.dom.addEventListener("keydown", handleKeyDown);
      editorView.dom.addEventListener("keyup", handleKeyUp);
      editorView.dom.addEventListener("blur", handleBlur);

      return {
        destroy() {
          // Clean up listeners when editor is destroyed
          editorView.dom.removeEventListener("keydown", handleKeyDown);
          editorView.dom.removeEventListener("keyup", handleKeyUp);
          editorView.dom.removeEventListener("blur", handleBlur);
          viewShiftState.delete(editorView);
        },
      };
    },

    props: {
      // Handle paste to check shift key state from the event itself
      handlePaste(view: EditorView, event: ClipboardEvent, slice: Slice): boolean {
        try {
          // Size limit check (1MB) to prevent memory issues with very large pastes
          const MAX_PASTE_SIZE = 1_000_000;
          const sliceSize = slice.content.size;
          if (sliceSize > MAX_PASTE_SIZE) {
            console.warn(
              `[PasteStrip] Paste exceeds size limit (${sliceSize} > ${MAX_PASTE_SIZE}), falling back to browser default`
            );
            return false;
          }

          // Check shift key from the clipboard event (most reliable)
          // Fall back to tracked state if event doesn't have shiftKey
          const shiftKeyFromEvent = "shiftKey" in event && Boolean(event.shiftKey);
          const shiftKeyFromState = viewShiftState.get(view) ?? false;
          const shouldPreserveFormatting = shiftKeyFromEvent || shiftKeyFromState;

          const transformedSlice = shouldPreserveFormatting
            ? transformWithFormatting(slice)
            : transformToPlainText(slice);

          // Insert the transformed content
          const tr = view.state.tr.replaceSelection(transformedSlice);
          view.dispatch(tr);

          // Return true to indicate we handled the paste
          return true;
        } catch (error) {
          console.warn("[PasteStrip] Paste handling failed, falling back to browser default:", error);
          // Let the browser handle the paste as a fallback
          return false;
        }
      },

      // clipboardTextParser is called for plain text pastes
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      clipboardTextParser(text: string, _$context): Slice {
        try {
          return textToSlice(text);
        } catch (error) {
          console.warn("[PasteStrip] Text parsing failed:", error);
          // Return empty slice on error - browser will handle fallback
          return new Slice(Fragment.empty, 0, 0);
        }
      },
    },
  });
}

/**
 * Transform a slice to plain text, stripping all formatting.
 */
function transformToPlainText(slice: Slice): Slice {
  const textContent = extractTextFromSlice(slice);

  if (!textContent.trim()) {
    return new Slice(Fragment.empty, 0, 0);
  }

  return textToSlice(textContent);
}

/**
 * Convert plain text to a ProseMirror slice with paragraphs.
 */
function textToSlice(text: string): Slice {
  if (!text.trim()) {
    return new Slice(Fragment.empty, 0, 0);
  }

  // Clean up excessive newlines while preserving paragraph breaks
  const cleanedText = text
    .replace(/\r\n/g, "\n") // Normalize line endings
    .replace(/\n{3,}/g, "\n\n") // Collapse excessive newlines
    .trim();

  // Split into paragraphs and create nodes
  const paragraphs = cleanedText.split(/\n\n+/);
  const nodes = paragraphs
    .map((p) => p.trim())
    .filter((p) => p.length > 0)
    .map((p) => {
      // Handle line breaks within paragraphs
      const parts = p.split(/\n/);
      if (parts.length === 1) {
        // Simple paragraph with just text
        return schema.node("paragraph", null, [schema.text(p)]);
      }
      // Paragraph with hard breaks
      const content: ReturnType<typeof schema.text | typeof schema.node>[] = [];
      parts.forEach((part, i) => {
        if (part) {
          content.push(schema.text(part));
        }
        if (i < parts.length - 1) {
          content.push(schema.node("hardBreak"));
        }
      });
      return schema.node("paragraph", null, content);
    });

  if (nodes.length === 0) {
    return new Slice(Fragment.empty, 0, 0);
  }

  return new Slice(Fragment.from(nodes), 0, 0);
}

/**
 * Transform a slice while preserving basic formatting (bold, italic, underline).
 * Also preserves heading levels (H1, H2, H3).
 * Used for Paste Special (Ctrl+Shift+V).
 */
function transformWithFormatting(slice: Slice): Slice {
  const nodes: ProseMirrorNode[] = [];

  slice.content.forEach((node) => {
    if (node.isTextblock) {
      // Process the text block preserving marks
      const content = processTextBlockWithMarks(node);

      // Determine the appropriate node type
      let nodeTypeName = "paragraph";
      let attrs: { level: number } | null = null;

      // Preserve heading levels 1-3 from pasted content
      if (node.type.name === "heading") {
        const nodeAttrs = node.attrs as { level?: unknown };
        if (typeof nodeAttrs.level === "number" && nodeAttrs.level >= 1 && nodeAttrs.level <= 3) {
          nodeTypeName = "heading";
          attrs = { level: nodeAttrs.level };
        }
      }
      // Preserve code_block from pasted content (code blocks contain only text, no marks)
      else if (node.type.name === "code_block") {
        // Code blocks only contain plain text, strip all marks
        const plainText = extractTextFromNode(node);
        if (plainText) {
          nodes.push(schema.node("code_block", null, [schema.text(plainText)]));
        } else {
          nodes.push(schema.node("code_block"));
        }
        return; // Skip the normal node creation below
      }

      if (content.length > 0) {
        nodes.push(schema.node(nodeTypeName, attrs, content));
      } else {
        // Empty block
        nodes.push(schema.node(nodeTypeName, attrs));
      }
    } else if (node.isText) {
      // Wrap loose text in a paragraph
      const marks = filterAllowedMarks(node.marks);
      const textNode = node.text
        ? schema.text(node.text, marks)
        : schema.text("");
      nodes.push(schema.node("paragraph", null, [textNode]));
    } else if (node.type.name === "blockquote") {
      // Preserve blockquote structure with recursively processed content
      const innerNodes: ProseMirrorNode[] = [];
      node.content.forEach((child) => {
        const childSlice = new Slice(Fragment.from(child), 0, 0);
        const processedSlice = transformWithFormatting(childSlice);
        processedSlice.content.forEach((n) => innerNodes.push(n));
      });
      if (innerNodes.length > 0) {
        nodes.push(schema.node("blockquote", null, innerNodes));
      }
    } else {
      // Recursively process other node types
      node.content.forEach((child) => {
        const childSlice = new Slice(Fragment.from(child), 0, 0);
        const processedSlice = transformWithFormatting(childSlice);
        processedSlice.content.forEach((n) => nodes.push(n));
      });
    }
  });

  // Merge consecutive empty paragraphs
  const mergedNodes = mergeEmptyParagraphs(nodes);

  if (mergedNodes.length === 0) {
    return new Slice(Fragment.empty, 0, 0);
  }

  return new Slice(Fragment.from(mergedNodes), 0, 0);
}

/**
 * Process a text block node, preserving allowed marks on its text content.
 */
function processTextBlockWithMarks(
  node: ProseMirrorNode
): Array<ReturnType<typeof schema.text | typeof schema.node>> {
  const content: Array<ReturnType<typeof schema.text | typeof schema.node>> =
    [];

  node.content.forEach((child) => {
    if (child.isText && child.text) {
      const marks = filterAllowedMarks(child.marks);
      content.push(schema.text(child.text, marks));
    } else if (child.type.name === "hardBreak") {
      content.push(schema.node("hardBreak"));
    }
    // Skip other inline nodes (images, etc.)
  });

  return content;
}

/**
 * Filter marks to only allow strong (bold), em (italic), and underline.
 * Uses actual schema mark type names.
 */
function filterAllowedMarks(marks: readonly Mark[]): Mark[] {
  // Use actual schema mark type names: strong, em, underline
  const allowedTypes = new Set(["strong", "em", "underline"]);

  return marks
    .filter((mark) => allowedTypes.has(mark.type.name))
    .map((mark) => {
      // Recreate marks with our schema to ensure compatibility
      const markType = schema.marks[mark.type.name];
      if (markType) {
        return markType.create(mark.attrs);
      }
      // If mark type doesn't exist in schema, use the original mark
      return mark;
    });
}

/**
 * Merge consecutive empty paragraphs into single empty paragraphs.
 */
function mergeEmptyParagraphs(nodes: ProseMirrorNode[]): ProseMirrorNode[] {
  const result: ProseMirrorNode[] = [];
  let lastWasEmpty = false;

  for (const node of nodes) {
    const isEmpty =
      node.type.name === "paragraph" && node.content.size === 0;
    if (isEmpty) {
      if (!lastWasEmpty) {
        result.push(node);
        lastWasEmpty = true;
      }
      // Skip consecutive empty paragraphs
    } else {
      result.push(node);
      lastWasEmpty = false;
    }
  }

  return result;
}

/**
 * Extract plain text from a single node (used for code blocks).
 */
function extractTextFromNode(node: ProseMirrorNode): string {
  let text = "";
  node.content.forEach((child) => {
    if (child.isText) {
      text += child.text ?? "";
    } else if (child.type.name === "hardBreak") {
      text += "\n";
    }
  });
  return text;
}

/**
 * Extract plain text from a slice, preserving paragraph structure.
 */
function extractTextFromSlice(slice: Slice): string {
  const parts: string[] = [];

  slice.content.forEach((node) => {
    if (node.isTextblock) {
      // For text blocks, get text content
      let blockText = "";
      node.content.forEach((child) => {
        if (child.isText) {
          blockText += child.text ?? "";
        } else if (child.type.name === "hardBreak") {
          blockText += "\n";
        }
      });
      parts.push(blockText);
    } else if (node.isText) {
      parts.push(node.text ?? "");
    } else {
      // Recurse into other node types
      node.content.forEach((child) => {
        const childSlice = new Slice(Fragment.from(child), 0, 0);
        parts.push(extractTextFromSlice(childSlice));
      });
    }
  });

  return parts.join("\n\n");
}

/**
 * Sanitize HTML content for safe paste.
 * Allows basic formatting tags, heading tags (h1, h2, h3), blockquote, and code blocks.
 */
export function sanitizeHtmlForPaste(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "b",
      "strong",
      "i",
      "em",
      "u",
      "h1",
      "h2",
      "h3",
      "blockquote",
      "pre",
      "code",
    ],
    ALLOWED_ATTR: [], // No attributes allowed
    KEEP_CONTENT: true, // Keep text content of removed tags
    ALLOW_DATA_ATTR: false, // Explicitly disallow data-* attributes
  });
}
